import {
  Directive,
  OnInit,
  PLATFORM_ID,
  Renderer2,
  computed,
  inject,
  input,
} from '@angular/core';
import { DOCUMENT, isPlatformServer } from '@angular/common';
import { FaqQuestionAndAnswer, InfoCarousel, InfoItem, M2A } from 'www-cms';
import { SS_URL } from './url.token';
import { HOST_ID } from './host.token';
import { generateFaqId } from '../shared/helpers/faq-id';
import { getInfoCarouselId } from '../shared/helpers/info-carousel-id';

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'ss-www-question-answer-schema',
  standalone: true,
})
export class QuestionAnswerSchemaDirective implements OnInit {
  buildingBlocks = input.required<M2A<any>[]>();
  private faqs = computed(() =>
    this.buildingBlocks()
      .filter((bb) => bb.collection === 'faqs')
      .map((bb) => bb.item.questionsAndAnswers as FaqQuestionAndAnswer[])
      .flat()
      .map((faq) => this.questionAndASchema(faq, this.getFaqUrl(faq)))
  );
  private infoCarouselWithFaqs = computed(() =>
    this.buildingBlocks()
      .filter(
        (bb) =>
          bb.collection === 'infoCarousel' &&
          (bb.item.infoItems as InfoItem[]).some((item) => item.faqsBlock)
      )
      .map((bb) => bb.item as InfoCarousel)
      .map((infoCarousel) =>
        infoCarousel.infoItems.map((item, index) =>
          item.faqsBlock!.questionsAndAnswers.map((faq) => ({
            ...faq,
            carouselId: infoCarousel.id,
            carouselIndex: index,
          }))
        )
      )
      .flat()
      .flat()
      .map((item) =>
        this.questionAndASchema(item, this.getInfoCarouselUrl(item))
      )
  );

  isServerSide = isPlatformServer(inject(PLATFORM_ID));
  hostId = inject(HOST_ID, { optional: true });
  url = inject(SS_URL, { optional: true });
  private renderer = inject(Renderer2);
  private _document = inject(DOCUMENT);

  ngOnInit(): void {
    if (
      this.isServerSide &&
      this.hostId &&
      this.url &&
      (this.faqs().length || this.infoCarouselWithFaqs().length)
    ) {
      const qaSchema = this.renderer.createElement('script');
      qaSchema.type = 'application/ld+json';
      qaSchema.text = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [...this.faqs()].concat(...this.infoCarouselWithFaqs()),
      });
      this.renderer.appendChild(this._document.head, qaSchema);
    }
  }

  private getInfoCarouselUrl(
    faq: FaqQuestionAndAnswer & { carouselId: number; carouselIndex: number }
  ) {
    return (
      this.hostId! +
      this.url! +
      '?' +
      getInfoCarouselId(faq.carouselId) +
      '=' +
      faq.carouselIndex +
      '#' +
      generateFaqId(faq.question)
    );
  }

  private getFaqUrl(faq: FaqQuestionAndAnswer) {
    return this.hostId! + this.url! + '#' + generateFaqId(faq.question);
  }

  private questionAndASchema(qAndA: FaqQuestionAndAnswer, url: string) {
    if (!this.hostId || !this.url || !this.isServerSide) {
      return;
    }
    return {
      '@type': 'Question',
      name: qAndA.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: qAndA.answer,
        url,
      },
    };
  }
}
